import { QuietErrorBoundary } from '@gonfalon/error-boundaries/QuietErrorBoundary';
import { useAgentPromptMutation } from '@gonfalon/observability-graphql/hooks';
import { Box } from '@gonfalon/observability-ui/components/Box';
import { O11Y_PORTAL_ID } from '@gonfalon/observability-ui/constants';
import { VegaProjectProvider } from '@gonfalon/observability-ui/hooks/VegaProjectProvider';
import type { AgentPromptMutationFn } from '@gonfalon/vega/components/AgentInsights/AgentInsights';
import { AgentInsightsPanel } from '@gonfalon/vega/components/AgentInsights/AgentInsightsPanel';

import { VegaWrappers } from './VegaWrappers';

/**
 * Shared panel body: renders children, the portal target, and the Vega drawer.
 * Callers supply the agent-prompt mutation — the default `agentPrompt` or one
 * routed to a dedicated endpoint (see AccountVegaPanel) — plus its sending
 * state, and may opt out of the account gen-AI gate. `nonGenAiSkill` defaults to
 * false so the generic VegaPanel stays gate-generic; only callers that route to
 * a gate-exempt endpoint pass true.
 */
export const VegaPanelBody = ({
  children,
  agentPromptMutation,
  sending,
  nonGenAiSkill = false,
}: React.PropsWithChildren<{
  agentPromptMutation: AgentPromptMutationFn;
  sending: boolean;
  nonGenAiSkill?: boolean;
}>) => {
  return (
    <>
      {children}
      <Box id={O11Y_PORTAL_ID} />
      <QuietErrorBoundary owner="team-o11y">
        <AgentInsightsPanel
          enableAgentResponseSelector
          agentPromptMutation={agentPromptMutation}
          sending={sending}
          nonGenAiSkill={nonGenAiSkill}
        />
      </QuietErrorBoundary>
    </>
  );
};

const VegaPanelInner = ({ children }: React.PropsWithChildren) => {
  const [agentPromptMutation, { loading: sending }] = useAgentPromptMutation();

  return (
    <VegaPanelBody agentPromptMutation={agentPromptMutation} sending={sending}>
      {children}
    </VegaPanelBody>
  );
};

export const VegaPanel = ({ children }: React.PropsWithChildren) => {
  return (
    <VegaWrappers>
      <VegaProjectProvider>
        <VegaPanelInner>{children}</VegaPanelInner>
      </VegaProjectProvider>
    </VegaWrappers>
  );
};
